import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  ETH_RELAY_TCP_SERVER_A_SAMPLE_HOST,
  ETH_RELAY_TCP_SERVER_A_SAMPLE_PORT,
  ethRelayTcpHexCommands,
} from '../../constants/poolAlertDeviceSamples';
import {
  fetchControlRoomGatewayConfig,
  fetchControlRoomGatewayStatus,
  saveControlRoomGatewayConfig,
  testControlRoomSiren,
  type ControlRoomGatewayStatus,
  type ControlRoomSirenDevice,
} from '../../services/controlRoomGatewayService';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';

type FormState = {
  id: number | null;
  label: string;
  host: string;
  port: string;
  location_hint: string;
  channel: string;
  tcp_start_command_warning: string;
  tcp_start_command_danger: string;
  tcp_stop_command: string;
  tcp_append_newline: boolean;
  socket_timeout_sec: string;
  sort_order: string;
  is_active: boolean;
  blink_enabled: boolean;
};

function emptyForm(nextId: number): FormState {
  const ch = Math.min(8, Math.max(1, nextId));
  const cmds = ethRelayTcpHexCommands(ch);
  return {
    id: null,
    label: `관제실 경광등 Ch${ch}`,
    host: ETH_RELAY_TCP_SERVER_A_SAMPLE_HOST,
    port: String(ETH_RELAY_TCP_SERVER_A_SAMPLE_PORT),
    location_hint: 'A모드 — 관제실 게이트웨이 listen 후 릴레이 전원 재기동',
    channel: String(ch),
    tcp_start_command_warning: cmds.start,
    tcp_start_command_danger: cmds.start,
    tcp_stop_command: cmds.stop,
    tcp_append_newline: false,
    socket_timeout_sec: '3',
    sort_order: String(ch),
    is_active: true,
    blink_enabled: false,
  };
}

function deviceToForm(d: ControlRoomSirenDevice): FormState {
  return {
    id: d.id,
    label: d.label || '',
    host: d.host || ETH_RELAY_TCP_SERVER_A_SAMPLE_HOST,
    port: d.port != null ? String(d.port) : String(ETH_RELAY_TCP_SERVER_A_SAMPLE_PORT),
    location_hint: d.location_hint || '',
    channel: String(d.sort_order || d.id || 1),
    tcp_start_command_warning: d.tcp_start_command_warning || '',
    tcp_start_command_danger: d.tcp_start_command_danger || '',
    tcp_stop_command: d.tcp_stop_command || '',
    tcp_append_newline: d.tcp_append_newline === true,
    socket_timeout_sec:
      d.socket_timeout_sec != null ? String(d.socket_timeout_sec) : '3',
    sort_order: String(d.sort_order ?? d.id ?? 0),
    is_active: d.is_active !== false,
    blink_enabled: d.blink_enabled === true,
  };
}

function formToDevice(form: FormState, existingId: number | null): ControlRoomSirenDevice {
  const ch = Math.min(8, Math.max(1, Number(form.channel) || 1));
  const port = Number(form.port) || ETH_RELAY_TCP_SERVER_A_SAMPLE_PORT;
  return {
    id: existingId != null && existingId > 0 ? existingId : 0,
    label: form.label.trim() || `관제실 경광등 Ch${ch}`,
    protocol: 'tcp_server',
    host: '0.0.0.0',
    port,
    location_hint: form.location_hint.trim() || undefined,
    template_key: `eth_relay_tcp_server_hex_ch${ch}`,
    tcp_append_newline: form.tcp_append_newline,
    tcp_start_command_warning: form.tcp_start_command_warning.trim(),
    tcp_start_command_danger: form.tcp_start_command_danger.trim(),
    tcp_stop_command: form.tcp_stop_command.trim(),
    socket_timeout_sec: Number(form.socket_timeout_sec) || 3,
    sort_order: Number(form.sort_order) || ch,
    is_active: form.is_active,
    blink_enabled: form.blink_enabled,
  };
}

type Props = {
  showSnackbar?: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
  ) => void;
};

/**
 * 관제실 경광등 등록 — 시설 「경광등/부저 A」와 동일(tcp_server listen + 채널 hex).
 * 서버 control_room_gateway.json devices 에 저장 → 에이전트가 동기화·실행.
 */
const ControlRoomSirenDeviceManagement: React.FC<Props> = ({ showSnackbar }) => {
  const notify = useCallback(
    (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
      if (showSnackbar) showSnackbar(message, severity);
    },
    [showSnackbar],
  );

  const [devices, setDevices] = useState<ControlRoomSirenDevice[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [tokenMasked, setTokenMasked] = useState('');
  const [newToken, setNewToken] = useState('');
  const [status, setStatus] = useState<ControlRoomGatewayStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(() => emptyForm(1));
  const [editingId, setEditingId] = useState<number | null>(null);
  const [testDurationSec, setTestDurationSec] = useState('3');
  const [testingKey, setTestingKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, st] = await Promise.all([
        fetchControlRoomGatewayConfig(),
        fetchControlRoomGatewayStatus(),
      ]);
      setDevices(Array.isArray(cfg.devices) ? cfg.devices : []);
      setEnabled(cfg.enabled !== false);
      setTokenMasked(cfg.agent_token_masked || '');
      setStatus(st);
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '관제실 경광등 설정을 불러오지 못함'));
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const persistDevices = async (
    nextDevices: ControlRoomSirenDevice[],
    extra?: { enabled?: boolean; agent_token?: string },
  ) => {
    setSaving(true);
    try {
      const body: {
        devices: ControlRoomSirenDevice[];
        enabled?: boolean;
        agent_token?: string;
      } = { devices: nextDevices };
      if (extra?.enabled !== undefined) body.enabled = extra.enabled;
      if (extra?.agent_token) body.agent_token = extra.agent_token;
      const cfg = await saveControlRoomGatewayConfig(body);
      setDevices(Array.isArray(cfg.devices) ? cfg.devices : nextDevices);
      setEnabled(cfg.enabled !== false);
      setTokenMasked(cfg.agent_token_masked || '');
      return cfg;
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    const used = new Set(devices.map((d) => d.id));
    let nextId = 1;
    while (used.has(nextId) && nextId < 99) nextId += 1;
    setEditingId(null);
    setForm(emptyForm(nextId));
    setDialogOpen(true);
  };

  const openEdit = (d: ControlRoomSirenDevice) => {
    setEditingId(d.id);
    setForm(deviceToForm(d));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const applyChannel = (channelStr: string) => {
    const ch = Math.min(8, Math.max(1, Number(channelStr) || 1));
    const cmds = ethRelayTcpHexCommands(ch);
    setForm((f) => ({
      ...f,
      channel: String(ch),
      label: f.label.match(/^관제실 경광등 Ch\d+$/)
        ? `관제실 경광등 Ch${ch}`
        : f.label,
      tcp_start_command_warning: cmds.start,
      tcp_start_command_danger: cmds.start,
      tcp_stop_command: cmds.stop,
      sort_order: String(ch),
    }));
  };

  const handleSaveDevice = async () => {
    const draft = formToDevice(form, editingId);
    if (!draft.tcp_start_command_warning || !draft.tcp_stop_command) {
      notify('시작/중지 hex 명령을 입력', 'warning');
      return;
    }
    let next: ControlRoomSirenDevice[];
    if (editingId != null) {
      next = devices.map((d) => (d.id === editingId ? { ...draft, id: editingId } : d));
    } else {
      next = [...devices, draft];
    }
    try {
      await persistDevices(next);
      notify(editingId != null ? '경광등 수정 저장' : '경광등 등록', 'success');
      closeDialog();
      const st = await fetchControlRoomGatewayStatus();
      setStatus(st);
    } catch (e: unknown) {
      notify(formatApiErrorMessage(e, '저장 실패'), 'error');
    }
  };

  const handleDelete = async (d: ControlRoomSirenDevice) => {
    if (!window.confirm(`「${d.label || d.id}」 삭제?`)) return;
    try {
      await persistDevices(devices.filter((x) => x.id !== d.id));
      notify('삭제함', 'success');
    } catch (e: unknown) {
      notify(formatApiErrorMessage(e, '삭제 실패'), 'error');
    }
  };

  const handleToggleEnabled = async (checked: boolean) => {
    try {
      await persistDevices(devices, { enabled: checked });
      setEnabled(checked);
      notify(checked ? '관제실 경광등 사용' : '관제실 경광등 중지(비활성)', 'info');
    } catch (e: unknown) {
      notify(formatApiErrorMessage(e, '활성 저장 실패'), 'error');
    }
  };

  const handleSaveToken = async () => {
    const t = newToken.trim();
    if (!t) {
      notify('새 토큰을 입력', 'warning');
      return;
    }
    try {
      await persistDevices(devices, { agent_token: t });
      setNewToken('');
      notify('에이전트 토큰 저장. 관제실 PC config.json 과 동일하게 맞출 것', 'success');
    } catch (e: unknown) {
      notify(formatApiErrorMessage(e, '토큰 저장 실패'), 'error');
    }
  };

  const runTest = async (deviceId?: number) => {
    const key = deviceId != null ? `row-${deviceId}` : 'all';
    setTestingKey(key);
    try {
      const dur = Math.min(60, Math.max(1, Number(testDurationSec) || 3));
      const r = await testControlRoomSiren({
        deviceId: deviceId ?? null,
        durationSec: dur,
      });
      notify(
        `테스트 울림 ${r.device_count ?? 1}대 · ${dur}초 후 자동 중지 (에이전트 온라인 필요)`,
        status?.agent_online ? 'success' : 'warning',
      );
    } catch (e: unknown) {
      notify(formatApiErrorMessage(e, '테스트 실패'), 'error');
    } finally {
      setTestingKey(null);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        시설 「경광등/부저 A」와 동일: 관제실 PC 게이트웨이가 TCP 서버로 listen(0.0.0.0:포트)하고,
        릴레이가 그 PC LAN IP로 접속. 채널마다 hex만 다르게 등록. 저장 내용은 서버에 두고
        에이전트가 주기적으로 받아 울림.
      </Alert>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              disabled={saving || loading}
              onChange={(e) => void handleToggleEnabled(e.target.checked)}
            />
          }
          label="관제실 경광등 사용"
        />
        <Chip
          size="small"
          label={
            status?.agent_online
              ? `에이전트 온라인 · 활성 ${status.active_device_count ?? 0}대`
              : '에이전트 오프라인'
          }
          color={status?.agent_online ? 'success' : 'default'}
        />
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          label="테스트(초)"
          size="small"
          type="number"
          inputProps={{ min: 1, max: 60 }}
          value={testDurationSec}
          onChange={(e) => setTestDurationSec(e.target.value)}
          sx={{ width: 110 }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={testingKey === 'all' ? <CircularProgress size={14} /> : <PlayArrowIcon />}
          disabled={testingKey != null || !devices.length}
          onClick={() => void runTest()}
        >
          전체 테스트
        </Button>
        <Button
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          disabled={loading}
          onClick={() => void load()}
        >
          새로고침
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          disabled={saving}
        >
          경광등 추가
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          에이전트 토큰: {tokenMasked || '—'}
        </Typography>
        <TextField
          size="small"
          label="새 토큰 (선택)"
          value={newToken}
          onChange={(e) => setNewToken(e.target.value)}
          sx={{ minWidth: 220 }}
          helperText="바꾸면 관제실 PC config.json 의 agent_token 도 동일하게"
        />
        <Button
          size="small"
          variant="outlined"
          disabled={!newToken.trim() || saving}
          onClick={() => void handleSaveToken()}
        >
          토큰 저장
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {loading && !devices.length ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>표시명</TableCell>
                <TableCell>Listen</TableCell>
                <TableCell>포트</TableCell>
                <TableCell>시작 hex</TableCell>
                <TableCell>활성</TableCell>
                <TableCell>Blink</TableCell>
                <TableCell align="right">관리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    등록된 관제실 경광등 없음. 「경광등 추가」로 등록.
                  </TableCell>
                </TableRow>
              ) : (
                devices.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.label || `id ${d.id}`}</TableCell>
                    <TableCell>{d.host || '0.0.0.0'}</TableCell>
                    <TableCell>{d.port}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {d.tcp_start_command_warning || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={d.is_active !== false ? 'ON' : 'OFF'}
                        color={d.is_active !== false ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={d.blink_enabled ? 'Blink' : '—'}
                        color={d.blink_enabled ? 'warning' : 'default'}
                        variant={d.blink_enabled ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={testingKey != null}
                        onClick={() => void runTest(d.id)}
                        aria-label="테스트"
                        title="테스트 울림"
                      >
                        {testingKey === `row-${d.id}` ? (
                          <CircularProgress size={18} />
                        ) : (
                          <PlayArrowIcon fontSize="small" />
                        )}
                      </IconButton>
                      <IconButton size="small" onClick={() => openEdit(d)} aria-label="수정">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => void handleDelete(d)}
                        aria-label="삭제"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: { xs: '70vh', sm: 560 },
            maxHeight: '92vh',
          },
        }}
      >
        <DialogTitle>
          {editingId != null ? '관제실 경광등 수정' : '관제실 경광등 등록'}
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
              pt: 1,
              pb: 1,
            }}
          >
            <TextField
              label="표시 이름"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              fullWidth
            />
            <TextField
              label="위치 안내"
              value={form.location_hint}
              onChange={(e) => setForm((f) => ({ ...f, location_hint: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="cr-ch">릴레이 채널</InputLabel>
              <Select
                labelId="cr-ch"
                label="릴레이 채널"
                value={form.channel}
                onChange={(e: SelectChangeEvent) => applyChannel(e.target.value)}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <MenuItem key={n} value={String(n)}>
                    Ch{n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Listen 포트"
              helperText="같은 보드면 포트 동일, 채널만 다르게"
              value={form.port}
              onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Listen bind"
              value="0.0.0.0"
              disabled
              helperText="고정. 릴레이 「서버 IP」에는 관제실 PC LAN IP"
              fullWidth
            />
            <TextField
              label="타임아웃(초)"
              value={form.socket_timeout_sec}
              onChange={(e) =>
                setForm((f) => ({ ...f, socket_timeout_sec: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="시작 명령 (경고)"
              value={form.tcp_start_command_warning}
              onChange={(e) =>
                setForm((f) => ({ ...f, tcp_start_command_warning: e.target.value }))
              }
              fullWidth
              sx={{ gridColumn: { sm: '1 / -1' } }}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
            <TextField
              label="시작 명령 (위험)"
              value={form.tcp_start_command_danger}
              onChange={(e) =>
                setForm((f) => ({ ...f, tcp_start_command_danger: e.target.value }))
              }
              fullWidth
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
            <TextField
              label="중지 명령"
              value={form.tcp_stop_command}
              onChange={(e) =>
                setForm((f) => ({ ...f, tcp_stop_command: e.target.value }))
              }
              fullWidth
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
            <FormControlLabel
              sx={{ alignItems: 'flex-start', mt: 0.5 }}
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, is_active: e.target.checked }))
                  }
                />
              }
              label="활성 (이벤트·테스트에 포함)"
            />
            <FormControlLabel
              sx={{ alignItems: 'flex-start', mt: 0.5 }}
              control={
                <Switch
                  checked={form.blink_enabled}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, blink_enabled: e.target.checked }))
                  }
                />
              }
              label="프로그램 Blink (장비 자체 blink 없을 때 시작/중지 약 1초 간격 반복)"
            />
            <FormControlLabel
              sx={{ alignItems: 'flex-start', mt: 0.5 }}
              control={
                <Switch
                  checked={form.tcp_append_newline}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, tcp_append_newline: e.target.checked }))
                  }
                />
              }
              label="명령 끝 LF"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSaveDevice()}
            disabled={saving}
          >
            {saving ? '저장 중…' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ControlRoomSirenDeviceManagement;
